import { useCallback, useEffect, useMemo, useRef } from "react";

import { useTradeInConfiguration } from "@jti/rest-api";
import type { Device } from "@jti/rest-api-types";
import { useDispatch, useSelector } from "@jti/store/reduxHooks";
import { updateTradeInOrderAttributes } from "@jti/store/slices/LEGACY__checkoutSlice";
import { useSetAdditionalAttributesMutation } from "@jti/store/slices/magentoApiSlice";
import { Select, useTranslations } from "@jti/ui";

import { useCartTradeInError } from "@checkout/pages/CartPage/components/hooks/useCartTradeInError";

type TradeInSelectProps = {
  sku: string;
};

const devicesToSelectOptionsAdapter = (devices?: Device[] | Pick<Device, "deviceId" | "deviceName">[]) => {
  if (!devices) {
    return [];
  }

  return devices.map(({ deviceId, deviceName }) => ({
    value: deviceId,
    label: deviceName ?? deviceId,
  }));
};

export const TradeInSelect = ({ sku }: TradeInSelectProps) => {
  const { __ } = useTranslations();
  const dispatch = useDispatch();
  const hasAutoSelectedRef = useRef(false);
  const cartId = useSelector((state) => state.cart.id);
  const [setAdditionalAttributes] = useSetAdditionalAttributesMutation();

  const { errorMessage, setError } = useCartTradeInError();

  const { devices = [], isUsingRegisteredDevices, isLoading } = useTradeInConfiguration();

  const selectAdaptedDevices = useMemo(() => devicesToSelectOptionsAdapter(devices), [devices]);
  const preselectedDevice = selectAdaptedDevices.length === 1 && !isLoading ? selectAdaptedDevices[0] : undefined;

  const onValueChange = useCallback(
    (newValue: unknown) => {
      const { value, label } = newValue as { value: string; label: string };

      const serialNumber = isUsingRegisteredDevices ? value : "";

      setError(undefined);
      dispatch(
        updateTradeInOrderAttributes({
          traded_in_new_sku: sku,
          traded_in_model: label,
          traded_in_serial_number: serialNumber,
        })
      );

      if (cartId) {
        setAdditionalAttributes({
          cartId,
          additionalAttributes: [
            { attribute_code: "traded_in_new_sku", value: sku },
            { attribute_code: "traded_in_model", value: label },
            { attribute_code: "traded_in_serial_number", value: serialNumber },
          ],
        });
      }
    },
    [dispatch, sku, isUsingRegisteredDevices, setError, cartId, setAdditionalAttributes]
  );

  useEffect(() => {
    if (preselectedDevice && !hasAutoSelectedRef.current) {
      hasAutoSelectedRef.current = true;
      onValueChange(preselectedDevice);
    }
  }, [preselectedDevice, onValueChange]);

  return (
    <Select
      aria-label={__("cart.cart_item.trade_in.select.placeholder")}
      placeholder={__("cart.cart_item.trade_in.select.placeholder")}
      noOptionsMessage={() => __("cart.cart_item.trade_in.select.no_options")}
      options={selectAdaptedDevices}
      isLoading={isLoading}
      onChange={onValueChange}
      isError={!!errorMessage}
      errorText={errorMessage}
      {...(preselectedDevice
        ? {
            value: preselectedDevice,
            isHelperTextVisible: true,
            helperText: __("cart.cart_item.trade_in.select.single_device_helper_text"),
            isDisabled: true,
          }
        : {
            isHelperTextVisible: !!errorMessage,
          })}
    />
  );
};
